import { create } from "zustand";
import type { DossierAssistantPlacement } from "@/features/hector-agent-casediscovery-v3/components/DossierCaseDiscoveryShell";

/**
 * Ouverture du panneau assistant docké, sortie du state local de `CaseView`.
 *
 * WHY un store : l'état d'ouverture vivait en `useState` DANS `CaseView`, donc
 * aucune autre surface ne pouvait ouvrir le dock. Les barres « Demander à
 * Hector » de Personnalisation / Réglages n'avaient d'autre choix que de
 * naviguer vers `/assistant`, ce qui éjecte l'avocat de l'écran qu'il est en
 * train de régler. Le store lève cet état au niveau application ; les deux
 * points de montage (page dossier et shell d'app) n'en sont plus que des
 * lecteurs.
 *
 * `caseId` porte le contexte ET décide du point de montage :
 * - `string` → dock du dossier, monté dans le split redimensionnable de
 *   `CaseView` (le contenu du dossier doit rétrécir avec lui) ;
 * - `null` → dock hors dossier, monté dans `DashboardLayout` pour les pages de
 *   type réglages.
 * Les deux prédicats sont donc mutuellement exclusifs par construction.
 *
 * ENVOI UNIQUE (décision produit 2026-08-12) — `prompt` n'est PAS un brouillon :
 * il est **envoyé** par le shell dès qu'il est appliqué. La barre « Demander à
 * Hector » d'un écran de réglage EST déjà le composer : l'avocat y a écrit sa
 * demande et appuyé sur envoyer. Lui faire ré-appuyer sur envoyer dans le dock
 * était un second geste sans information nouvelle — de la friction, pas une
 * sécurité. Cette exception est bornée à ce canal : `V3ChatInterface.composerDraft`
 * (wizard « Numéroter avec l'IA ») reste un vrai brouillon sans envoi.
 */
export interface AssistantDockPrompt {
  content: string;
  /**
   * Incrémenté à chaque pose — distingue deux demandes de texte identique, et
   * sert de **clé d'idempotence** au shell puis à `V3ChatInterface` : un envoi
   * automatique ne doit partir qu'une fois, malgré le double-invoke StrictMode
   * et les re-rendus du dock.
   */
  nonce: number;
}

export interface OpenAssistantDockOptions {
  /**
   * Dossier hôte du dock, ou `null` pour le dock hors dossier. Explicite et
   * obligatoire : c'est ce champ qui choisit le point de montage, un défaut
   * implicite ferait ouvrir le mauvais panneau sans le dire.
   */
  caseId: string | null;
  /** Demande cadrée par l'hôte, **envoyée** à l'application (cf. docstring). */
  prompt?: string;
}

interface AssistantDockStore {
  caseId: string | null;
  isOpen: boolean;
  placement: DossierAssistantPlacement;
  prompt: AssistantDockPrompt | null;
  openAssistantDock: (options: OpenAssistantDockOptions) => void;
  close: () => void;
  setPlacement: (placement: DossierAssistantPlacement) => void;
  /** Rend la demande courante et la retire, pour qu'une réouverture ne la rejoue pas. */
  consumePrompt: () => AssistantDockPrompt | null;
}

/**
 * Le dock HORS dossier est-il ouvert ?
 *
 * Deux familles de lecteurs : son point de montage (`DashboardLayout`) et les
 * barres « Demander à Hector » des écrans de Personnalisation, qui s'effacent
 * tant qu'il l'est — la conversation vit alors dans le dock, et deux zones de
 * saisie concurrentes à l'écran sont précisément la confusion qu'on retire.
 */
export const selectIsCaselessAssistantDockOpen = (state: AssistantDockStore): boolean =>
  state.isOpen && state.caseId === null;

let promptNonceCounter = 0;

export const useAssistantDockStore = create<AssistantDockStore>()((set, get) => ({
  caseId: null,
  isOpen: false,
  placement: "docked",
  prompt: null,
  openAssistantDock: ({ caseId, prompt }) => set((state) => {
    // Un panneau DÉJÀ ouvert sur le même contexte garde sa position : une
    // seconde barre « Demander à Hector » pose une demande, elle ne rattache
    // pas une fenêtre que l'avocat vient de détacher. Une vraie (ré)ouverture
    // repart dockée — la fenêtre flottante est un choix par panneau, pas une
    // préférence persistante (comportement historique du bouton de CaseView).
    const reusesOpenPanel = state.isOpen && state.caseId === caseId;
    return {
      caseId,
      isOpen: true,
      placement: reusesOpenPanel ? state.placement : "docked",
      // Une ouverture SANS demande efface celle qui n'aurait pas été consommée.
      // Depuis que `prompt` part tout seul, une demande survivante est un
      // message que personne ne relit : le bouton « Demander à Hector » d'un
      // DOSSIER enverrait alors dans son fil une demande cadrée pour un écran
      // de réglages. Le shell consomme dans le commit qui le monte, donc ce
      // nettoyage ne peut jamais annuler une demande légitime.
      prompt: prompt === undefined ? null : { content: prompt, nonce: ++promptNonceCounter },
    };
  }),
  // Fermer remet l'état au repos complet (dont `caseId`) : un dock fermé ne doit
  // laisser aucun contexte résiduel qu'une garde de frontière devrait re-nettoyer.
  close: () => set({ caseId: null, isOpen: false, placement: "docked", prompt: null }),
  setPlacement: (placement) => set({ placement }),
  consumePrompt: () => {
    const { prompt } = get();
    if (prompt) set({ prompt: null });
    return prompt;
  },
}));
